/**
 * Generic composable for loading embed data (by ID or directly)
 * Works with any AssetListItem type
 */

import { ref, computed, watch, type Ref, type ComputedRef } from 'vue'
import { readEmbed } from '@/api/visitor/embeds'
import { transformEmbed } from '../../types/transforms'
import type { Embed } from '../../types/embed'
import type { AssetListItem } from '../../types/asset'
import { useInjectedEmbedAudience, useInjectedEmbedLoaderApi, type EmbedLoaderApiFn } from './usePlayerContext'
import { embedFailureOf, type EmbedAudience, type EmbedFailure } from '../embedFailure'

/**
 * The `visitor` surface's embed read, presenting the opening grant an embed page handed its
 * player — the embedding site and the password the page verified — on every call.
 *
 * **A second read of one publication re-opens the sitting the first one opened**: the loader keeps
 * the `session_id` each read answered and sends it back on the next read of that uid (a `reload`
 * command), which the server answers by leaving that session as it is rather than counting a
 * second opening.
 */
export function publicEmbedLoader(opening?: string): EmbedLoaderApiFn {
  const sittings = new Map<string, string>()
  return async (id, params) => {
    const view = await readEmbed(
      id,
      {
        token: params.token,
        password: params.password,
        viewer_id: params.viewerId,
        session_id: sittings.get(id),
        preset: params.preset,
      },
      opening,
    )
    if (view.session_id) sittings.set(id, view.session_id)
    return transformEmbed(view)
  }
}

/** The `visitor` surface's embed read with no grant: what an embed opens with outside its page. */
export const loadPublicEmbed: EmbedLoaderApiFn = publicEmbedLoader()

export interface UseEmbedLoaderOptions<T extends AssetListItem = AssetListItem> {
  /** Embed ID to load */
  embedId?: Ref<string | undefined>
  /** Embed data (if already loaded) */
  embed?: Ref<Embed<T> | undefined>
  /** Access token for token-required embeds */
  token?: string
  /** Link password for a password-protected share opened through its embed page */
  password?: string
  /** Viewer ID for analytics */
  viewerId?: string
  /** The preset to resolve the look with — see `EmbedLoaderParams.preset` */
  preset?: string
  /** Auto-load embed when embedId changes (default: true) */
  autoLoad?: boolean
  /** Custom API function for loading data (overrides default and injected) */
  apiFn?: EmbedLoaderApiFn
  /** Who reads a failure, travelling with `apiFn` (overrides the injected audience) */
  audience?: EmbedAudience
}

export interface UseEmbedLoaderReturn<T extends AssetListItem = AssetListItem> {
  /** The loaded embed data */
  embed: ComputedRef<Embed<T> | undefined>

  /** Loading state */
  isLoading: Ref<boolean>

  /** Error message if load failed — the API's English sentence, for logs; never shown */
  error: Ref<string | undefined>

  /** What a failed load means to a visitor, which the refusal screen words */
  failure: Ref<EmbedFailure | undefined>

  /** Load embed by ID */
  load: (embedId: string) => Promise<Embed<T>>

  /** Set embed data directly */
  setEmbed: (embed: Embed<T>) => void

  /** Clear embed data */
  clear: () => void
}

/**
 * Generic composable for loading embed data.
 * Works with any AssetListItem type (Video, Audio, Image, Document).
 *
 * The API function is resolved in this order:
 * 1. `options.apiFn` (explicit override)
 * 2. Injected via `provideEmbedLoaderApi()` (from parent component)
 * 3. Default: `loadPublicEmbed` (the `visitor` embed read)
 *
 * @example
 * ```typescript
 * // For images
 * const loader = useEmbedLoader<ImageAssetListItem>({
 *   embedId: toRef(props, 'embedId'),
 * })
 *
 * // For audio
 * const loader = useEmbedLoader<AudioAssetListItem>({
 *   embed: toRef(props, 'embed'),
 * })
 * ```
 */
export function useEmbedLoader<T extends AssetListItem = AssetListItem>(
  options: UseEmbedLoaderOptions<T> = {},
): UseEmbedLoaderReturn<T> {
  const { embedId, embed: embedProp, token, password, viewerId, preset, autoLoad = true, apiFn } = options

  const injectedApiFn = useInjectedEmbedLoaderApi()
  // The injected audience belongs to the injected loader, so an explicit `apiFn` without its own
  // audience does not borrow it.
  const injectedAudience = useInjectedEmbedAudience()
  const audience = options.audience ?? (apiFn ? 'visitor' : injectedAudience)
  const resolvedApiFn = apiFn ?? injectedApiFn ?? loadPublicEmbed

  const loadedEmbed = ref<Embed<T> | undefined>()
  const isLoading = ref(false)
  const error = ref<string | undefined>()
  const failure = ref<EmbedFailure | undefined>()

  // Computed embed - returns prop if provided, otherwise loaded
  const embed = computed(() => {
    return embedProp?.value ?? loadedEmbed.value
  })

  /**
   * Load embed by ID
   */
  async function load(id: string): Promise<Embed<T>> {
    isLoading.value = true
    error.value = undefined
    failure.value = undefined

    try {
      // The player that asked for `T` is the one that knows which kind of embed it opens; a
      // loader answers the general shape.
      const data = (await resolvedApiFn(id, { token, password, viewerId, preset })) as Embed<T>
      loadedEmbed.value = data
      return data
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load embed'
      failure.value = embedFailureOf(err, audience)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Set embed data directly
   */
  function setEmbed(data: Embed<T>): void {
    loadedEmbed.value = data
    error.value = undefined
    failure.value = undefined
  }

  /**
   * Clear embed data
   */
  function clear(): void {
    loadedEmbed.value = undefined
    error.value = undefined
    failure.value = undefined
  }

  // Auto-load when embedId changes
  if (autoLoad && embedId) {
    watch(
      embedId,
      (newId) => {
        if (newId) {
          load(newId)
        } else {
          clear()
        }
      },
      { immediate: true },
    )
  }

  return {
    embed,
    isLoading,
    error,
    failure,
    load,
    setEmbed,
    clear,
  }
}
